import { FC, useEffect, useState, useRef } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { api } from "@/helper/api";
import FindingForm from "@/components/forms/findingForm";
import { useTranslation } from "react-i18next";
import { useErrorModal } from "@/components/forms/errorHandling/useErrorModal";
import GenericNotification from "@/components/notification/genericNotification";
import styles from "@/components/appointmentDashboard/appointmentTreeForm.module.css";
import { useConfirmationModal } from "@/context/confirmationModalContext";
import useCompactFormTitle from "@/helper/customHooks/useCompactFormTitle";
import classNames from "classnames";

export interface formValues {
  id?: string;
  created_by?: string;
  modified_by?: string;
  created_on?: string;
  modified_on?: string;
  unit?: string;
  name?: string;
  value?: number;
  appointment?: string;
  findings?: [];
  image_set?: any[];
  note?: any;
}

interface Props {
  onStatusUpdated: (newStatus: string) => void;
  onFormSubmitted: () => void;
  statusOfAppointment: string;
}

const AppointmentTreeForm: FC<Props> = ({
  onStatusUpdated,
  onFormSubmitted,
  statusOfAppointment,
}) => {
  const { t } = useTranslation();
  const compactTitle = useCompactFormTitle();
  const [formValues, setFormValues] = useState<formValues[]>([]);
  const [measurementsLoaded, setMeasurementsLoaded] = useState<boolean>(false);
  const [navigateBackAfterSubmit, setNavigateBackAfterSubmit] =
    useState<boolean>(false);
  const [formName, setFormName] = useState<string>("");
  const { handleError, errorModal } = useErrorModal();
  const [successForNotification, setSuccessForNotification] =
    useState<boolean>(false);
  const [notificationMessage, setNotificationMessage] = useState<any>("");
  const [notificationVariant, setNotificationVariant] = useState<
    "success" | "error" | "warning" | "info"
  >();
  const [
    formKeyForTriggerReloadAfterAdditionalUploads,
    setFormKeyForTriggerReloadAfterAdditionalUploads,
  ] = useState<number>(0);

  const timerRef = useRef<NodeJS.Timeout | null>(null);

  /*eslint-disable */
  const { appointmentId, formId, findingId } = useParams() as {
    appointmentId: string;
    formId: string;
    findingId: string;
  };
  /*eslint-enable */
  const navigate = useNavigate();
  const hostname = window.location.hostname;
  const subdomain = hostname.split(".")[0];

  const { showConfirmationModal } = useConfirmationModal();

  const updateAppointmentStatus = () => {
    if (!appointmentId) return;

    api.genericApiRequest({
      entity: "appointment",
      method: "put",
      entityId: appointmentId,
      successHandler: () => {
        setSuccessForNotification(true);
        setNotificationVariant("success");
        setNotificationMessage(t("appointment_status_updated_to_in_progress"));
        onStatusUpdated("in_progress");
      },
      failHandler: (error: any) => {
        console.error(
          "Failed to update appointment status to in_progress:",
          error
        );
        handleError(error);
      },
      submitData: {
        status: "in_progress",
      },
    });
  };

  useEffect(() => {
    if (
      appointmentId &&
      subdomain === "admin" &&
      (statusOfAppointment === "open" ||
        statusOfAppointment === "prefilled_by_user")
    ) {
      timerRef.current = setTimeout(updateAppointmentStatus, 10000); // 10 seconds
    }

    return () => {
      if (timerRef.current) {
        clearTimeout(timerRef.current);
        timerRef.current = null;
      }
    };
  }, [appointmentId, statusOfAppointment]);

  const getMeasurements = (appointmentId: any) => {
    const additionalUrlParameters: any = {};
    additionalUrlParameters["linked_appointment_id"] = appointmentId;
    additionalUrlParameters["include_images_and_notes"] = true;
    api.genericApiRequest({
      entity: "measurement",
      method: "get",
      getAllPages: true,

      successHandler: (res: any) => {
        setFormValues(res.data.results);
        setMeasurementsLoaded(true);
      },
      failHandler: (error: any) => {
        handleError(error);
      },
      parametersToRender: {
        depth: "0",
        additionalUrlParameters: additionalUrlParameters,
      },
    });
  };
  useEffect(() => {
    if (!findingId) {
      showConfirmationModal(t("confirm_error_no_finding"));
      return;
    }
    getMeasurements(appointmentId);
  }, [formKeyForTriggerReloadAfterAdditionalUploads, formId, findingId]);

  const readOnly = false;

  const handleNavigateBack = () => {
    navigate("/appointmentDashboard/" + appointmentId);
  };
  const reloadForm = () =>
    setFormKeyForTriggerReloadAfterAdditionalUploads((v) => v + 1);

  useEffect(() => {
    if (navigateBackAfterSubmit) {
      handleNavigateBack();
    }
  }, [navigateBackAfterSubmit]);

  const resetStateOnCloseNotification = () => {
    setSuccessForNotification(false);
  };

  return (
    <>
      {formId && (
        <div className={styles.formLayout}>
          <h2 className={classNames(styles.formName, { [styles.compactTitle]: compactTitle })}>
            {formName}
          </h2>
          <FindingForm
            statusOfAppointment={statusOfAppointment}
            withPaper={true}
            findingId={findingId}
            formId={formId}
            formValues={formValues}
            formValuesLoaded={measurementsLoaded}
            onAdditionalUploadSuccess={reloadForm}
            readOnly={readOnly}
            pageLayout={true}
            guardUnsavedChanges={true}
            onSuccessfulFormSubmitHandler={() => {
              onFormSubmitted();
              setNavigateBackAfterSubmit(true);
            }}
            onCancelFormHandler={() => {
              handleNavigateBack();
            }}
            onFormTitleChange={(formTitle: any) => {
              setFormName(formTitle);
            }}
          />
        </div>
      )}
      {errorModal}

      <GenericNotification
        message={successForNotification ? notificationMessage : null}
        variant={notificationVariant}
        handleCloseSnackbar={resetStateOnCloseNotification}
      />
    </>
  );
};

export default AppointmentTreeForm;
